import { useReplaceProductInPlanMutation } from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import { getPlanMonthList } from '@/core/utils/getPlanMonthList.ts'
import { useAccountBranchIdsParams } from '@/core/hooks/useAccountBranchIdsParams.ts'
import { mapReplenishmentPlanItemFragment } from '@/data/replenishmentPlan/mappers/mapReplenishmentPlanItemFragment.ts'
import { useDispatch, useSelector } from 'react-redux'
import { selectSearch } from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import { replenishmentPlanActions } from '@/data/replenishmentPlan/slice/replenishmentPlan.slice.ts'
import { useSearchProducts } from '@/data/replenishmentPlan/hooks/useSearchProducts.ts'
import { useCallback } from 'react'
import { useGetPlanItemsForecastsShipments } from '@/data/replenishmentPlan/hooks/useGetPlanItemsForecastsShipments.ts'

const useReplaceProduct = () => {
  const [replaceProductInPlanMutation, { isLoading }] =
    useReplaceProductInPlanMutation()

  const { branchId } = useAccountBranchIdsParams()

  const dispatch = useDispatch()

  const search = useSelector(selectSearch)

  const { searchProducts } = useSearchProducts()

  const { getPlanItemsForecastsShipments } = useGetPlanItemsForecastsShipments()

  const replaceProduct = useCallback(
    async (oldProductId: number, newProductId: number) => {
      const { fromMonthFormatted, toMonthFormatted } = getPlanMonthList()

      try {
        const data = await replaceProductInPlanMutation({
          branchId,
          oldProductId,
          newProductId,
        }).unwrap()

        if (!data || !data.replenishmentPlanProductReplace) {
          return
        }

        const { result, errors } = data.replenishmentPlanProductReplace

        if (errors) {
          throw new Error(errors[0])
        }

        if (!result) {
          return
        }

        const { planItems, forecasts, shipments } =
          await getPlanItemsForecastsShipments({
            branchId,
            fromMonth: fromMonthFormatted,
            toMonth: toMonthFormatted,
            productIds: [newProductId],
          })

        const mapResult = mapReplenishmentPlanItemFragment(
          result.product,
          forecasts,
          planItems,
          shipments,
        )

        dispatch(
          replenishmentPlanActions.replaceProductPlan({
            oldProductId,
            newProduct: {
              ...mapResult.planProduct,
              isInPlan: true,
              salableProductId: result.salableProductId,
            },
            newProductForecastItems: mapResult.forecastItems,
            newProductPlannedItems: mapResult.plannedItems,
            newProductShipmentItems: mapResult.shipmentItems,
          }),
        )

        // Refetch products if there is a search term
        if (search) {
          await searchProducts(search)
        }

        // Notify the host app to refresh cart data (same window now that the plan is inline)
        window.postMessage({ type: 'CART_INVALIDATED' }, '*')
      } catch (e) {
        console.error(e)

        throw e
      }
    },
    [
      branchId,
      dispatch,
      getPlanItemsForecastsShipments,
      replaceProductInPlanMutation,
      search,
      searchProducts,
    ],
  )

  return { replaceProduct, isLoading }
}

export { useReplaceProduct }
